import React, { useCallback } from 'react';

import * as actions from '../NhiActions';

import { SUBCATEGORIES } from '../../../common/categories';
import { NHI_FIELDS } from '../../../common/fields';
import { conditions, entity } from '../../../../../sharedComponents/scope/types';
import { SCOPES_OPTIONS } from '../../../../../sharedComponents/scope/consts';
import Scope from '../../../../../sharedComponents/scope/Scope';
import { TAB_OPTIONS } from '../../../../../sharedComponents/EntitiesDualSelectList';

const { NHI_ON_PREM } = SUBCATEGORIES;

interface NhiCustomInsightProps {
    save: () => void;
    fields: any;
    dispatch: React.Dispatch<any>;
}

const NhiCustomInsight: React.FC<NhiCustomInsightProps> = React.memo(({ save, dispatch, fields }) => {
    const customInsight = fields[NHI_ON_PREM.ID].fields.customInsight;

    const onDeleteEntity = useCallback(
        (insight: conditions | entity, scope) => {
            if ('conditionString' in insight && 'conditionType' in insight) {
                const filed =
                    scope === SCOPES_OPTIONS.IN_SCOPE
                        ? NHI_FIELDS.INCLUDE_NAMING_CONVENTIONS.ID
                        : NHI_FIELDS.EXCLUDE_NAMING_CONVENTIONS.ID;
                dispatch({
                    type: actions.REMOVE_CUSTOM_INSIGHT_ARRAY_ITEM,
                    payload: {
                        fieldId: filed,
                        value: { conditionString: insight.conditionString, conditionType: insight.conditionType }
                    }
                });
                save();
            } else if ('guid' in insight) {
                const field =
                    scope === SCOPES_OPTIONS.IN_SCOPE
                        ? NHI_FIELDS.INCLUDE_USER_AND_GROUP.ID
                        : NHI_FIELDS.EXCLUDE_USER_AND_GROUP.ID;
                dispatch({
                    type: actions.REMOVE_ENTITY,
                    payload: {
                        fieldId: field,
                        value: { guid: insight.guid }
                    }
                });
                save();
            }
        },
        [dispatch, save]
    );
    const onAddNamingConvention = useCallback(
        (conditionString: string, conditionType: number, scope: number) => {
            const filed =
                scope === SCOPES_OPTIONS.IN_SCOPE
                    ? NHI_FIELDS.INCLUDE_NAMING_CONVENTIONS.ID
                    : NHI_FIELDS.EXCLUDE_NAMING_CONVENTIONS.ID;
            dispatch({
                type: actions.Add_TO_CUSTOM_INSIGHT_ARRAY,
                payload: {
                    fieldId: filed,
                    value: { conditionString, conditionType }
                }
            });
            save();
        },
        [dispatch, save]
    );
    const onEditNamingConvention = useCallback(
        (oldNamingConvention: conditions, newNamingConvention: conditions, scope: number) => {
            const filed =
                scope === SCOPES_OPTIONS.IN_SCOPE
                    ? NHI_FIELDS.INCLUDE_NAMING_CONVENTIONS.ID
                    : NHI_FIELDS.EXCLUDE_NAMING_CONVENTIONS.ID;
            dispatch({
                type: actions.EDIT_CUSTOM_INSIGHT_ARRAY_ITEM,
                payload: {
                    fieldId: filed,
                    value: {
                        oldConditionString: oldNamingConvention.conditionString,
                        oldConditionType: oldNamingConvention.conditionType,
                        newConditionString: newNamingConvention.conditionString,
                        newConditionType: newNamingConvention.conditionType
                    }
                }
            });
            save();
        },
        [dispatch, save]
    );

    const onSelectEntities = useCallback(
        async (entityData: entity[], scope: number) => {
            const values = entityData?.map((item) => ({
                name: item.name,
                guid: item.guid,
                domain_name: item.domain_name,
                path: item.path,
                is_disabled: item.is_disabled,
                type: item.type
            }));
            const filed =
                scope === SCOPES_OPTIONS.IN_SCOPE
                    ? NHI_FIELDS.INCLUDE_USER_AND_GROUP.ID
                    : NHI_FIELDS.EXCLUDE_USER_AND_GROUP.ID;
            dispatch({
                type: actions.ADD_ENTITY,
                payload: {
                    fieldId: filed,
                    values: values
                }
            });
            save();
        },
        [dispatch, save]
    );

    return (
        <div className="w-full ">
            <div>
                <div className=" font-medium text-sm pb-1">Service Account Detection</div>
                <div className="text-xs text-sf-gray-500 pb-4">
                    Influence service account detection by providing certain naming conventions, AD groups, or OUs your
                    organization uses to manage service accounts.
                </div>
            </div>
            <div className="w-full grid grid-cols-2 gap-4">
                <Scope
                    entities={customInsight?.usersAndGroups}
                    conditions={customInsight?.conditions}
                    scope={SCOPES_OPTIONS.IN_SCOPE}
                    emptyStateTitle="None included"
                    emptyStateDescription="Add naming conventions or AD objects"
                    onAddNamingConvention={onAddNamingConvention}
                    onEditNamingConvention={onEditNamingConvention}
                    onSelectEntities={onSelectEntities}
                    deleteEntity={onDeleteEntity}
                    tabOptions={[TAB_OPTIONS.OUS, TAB_OPTIONS.GROUPS, TAB_OPTIONS.USERS]}></Scope>

                <Scope
                    entities={customInsight?.excludedUsersAndGroups}
                    conditions={customInsight?.excludedConditions}
                    scope={SCOPES_OPTIONS.OUT_SCOPE}
                    emptyStateTitle="None excluded"
                    emptyStateDescription="Add naming conventions or AD objects"
                    onAddNamingConvention={onAddNamingConvention}
                    onEditNamingConvention={onEditNamingConvention}
                    onSelectEntities={onSelectEntities}
                    deleteEntity={onDeleteEntity}
                    tabOptions={[TAB_OPTIONS.OUS, TAB_OPTIONS.GROUPS, TAB_OPTIONS.USERS]}></Scope>
            </div>
        </div>
    );
});

export default NhiCustomInsight;
